import React, { useState, useCallback, useEffect } from "react";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { Stack, InputLabel, TextField, Autocomplete } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { theme } from "venuescanner-js/theme";
import { locations } from "venuescanner-js/vanilla/components/locations";

import { MapPinIcon } from "venuescanner-js/react/components/Objects/Icons/FeatherIcons";

const AutoCompleteLocation = styled(Autocomplete)(() => ({
  minWidth: "210px",

  [theme.breakpoints.up("md")]: {
    minWidth: "310px",
  },

  [".MuiAutocomplete-inputRoot:before"]: {
    borderBottom: "none",
  },
}));

const GroupHeader = styled("div")(() => ({
  position: "sticky",
  top: "-8px",
  padding: "2px 8px",
  textTransform: "uppercase",
  backgroundColor: "#FFF",
}));

const GroupItems = styled("ul")({
  padding: 0,

  [".MuiAutocomplete-option"]: {
    minHeight: "32px",
  },
});

const ErrorTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.red,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.red,
    fontSize: "12px",
    fontWeight: "400",
    letterSpacing: "0.15px",
    lineHeight: "18px",
  },
}));

const LocationFieldMui = ({ onFocus, error, control }) => {
  const [options, setOptions] = useState([]);
  const [locationInputValue, setLocationInputValue] = useState("");

  if (!error) {
    error = { show: false, message: "" };
  }

  const getLocationOptionsAsync = async (query) => {
    const fetchedLocations = await locations.getResults()(query);
    const options = fetchedLocations.flatMap((el) => {
      const category = el.label;
      // FIXME: Removed category venue from results, it would be better if endpoint doesn't provide venues..
      if (category === "venue") {
        return [];
      }
      return el.options.map((option) => ({
        category,
        key: `${category}-${option.value}`,
        label: option.label.replace(/(<([^>]+)>)/gi, ""),
        kind: option.kind,
        value: option.value,
      }));
    });

    return options;
  };

  const getLocationOptionsDelayed = useCallback(
    debounce((text, callback) => {
      setOptions([]);
      getLocationOptionsAsync(text).then(callback);
    }, 400),
    []
  );

  useEffect(() => {
    getLocationOptionsDelayed(locationInputValue, (filteredOptions) => {
      setOptions(filteredOptions);
    });
    return () => {
      getLocationOptionsDelayed.cancel();
    };
  }, [locationInputValue, getLocationOptionsDelayed]);

  return (
    <Controller
      name="location"
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <Stack direction="row" alignItems="center" spacing={2}>
          <MapPinIcon />
          <Stack>
            <InputLabel
              sx={{
                textTransform: "upperCase",
                marginBottom: { xs: "6px", md: "0px" },
              }}
              htmlFor="location-autocomplete"
            >
              location
            </InputLabel>
            <ErrorTooltip title={error.message} arrow open={error.show}>
              <AutoCompleteLocation
                {...field}
                id="location-autocomplete"
                forcePopupIcon={false}
                options={options}
                groupBy={(option) => option.category}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.label
                }
                getOptionLabel={(option) => option.label}
                filterOptions={(x) => x}
                loading={options.length === 0}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                }}
                onInputChange={(e, newInputValue) =>
                  setLocationInputValue(newInputValue)
                }
                renderInput={(params) => (
                  <TextField
                    onFocus={onFocus}
                    variant="standard"
                    placeholder="Location"
                    name={field.name}
                    {...params}
                  />
                )}
                renderGroup={(params) => (
                  <li>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
              />
            </ErrorTooltip>
          </Stack>
        </Stack>
      )}
    />
  );
};

LocationFieldMui.propTypes = {
  onFocus: PropTypes.func,
  control: PropTypes.object,
  error: PropTypes.object,
};

export default LocationFieldMui;
